// PARA-133 (AD-3, F-17) — карта иллюстраций: кадр × локаль → URL ассета.
//
// Импорты СТАТИЧЕСКИЕ и это принципиально: Vite подставляет хешированное имя
// файла (кеш-бастинг), кладёт PNG в dist, а отсутствующий файл роняет сборку —
// «мёртвая ссылка» на несуществующий кадр недостижима by design (EC-13).
// Динамический import(), fetch и frontend/public/ здесь запрещены (AD-3, AD-9).
//
// Сами PNG порождаются командой `npm run screenshots:faq` (e2e/faq-screenshots.
// spec.ts) под фиксированными именами; переименовывать их вручную нельзя — имена
// завязаны на ScreenshotId и на каталог src/assets/faq/{locale}/.
//
// ⚠️ ИСКЛЮЧЕНИЕ ИЗ «отдельных хешированных файлов» (измерено на сборке, ревью
// итерации 1): кадр `language-switcher` — фрагмент шапки 402×64, ~3.5 КБ, то
// есть МЕНЬШЕ дефолтного порога `build.assetsInlineLimit` Vite (4096 Б).
// Поэтому все четыре его локали встраиваются в основной JS-чанк строкой
// `data:image/png;base64`, а не выкладываются в dist/assets/ отдельными
// файлами: в сборке лежит 40 PNG, а не 44. Следствия — ровно два:
//   1. хеширование имени и кеш-бастинг на этих четырёх кадрах не работают;
//   2. ~19 КБ base64 приезжают каждому пользователю, включая тех, кто справку
//      не открывает, — то есть «прирост первого экрана 0 байт» верно для
//      остальных сорока кадров, но не для этих.
// Правка `vite.config.ts` (`assetsInlineLimit`) или переход на `?url` меняли бы
// поведение сборки ВСЕГО фронта ради четырёх файлов и в scope PARA-133 не
// входят. Фича при этом остаётся офлайновой, а бюджет AC-13 считается по
// исходному каталогу и соблюдён.

import backlogRu from '../../../assets/faq/ru/backlog.png';
import languageSwitcherRu from '../../../assets/faq/ru/language-switcher.png';
import taskFormRu from '../../../assets/faq/ru/task-form.png';
import taskDetailRu from '../../../assets/faq/ru/task-detail.png';
import procrastinationEnergyRu from '../../../assets/faq/ru/procrastination-energy.png';
import procrastinationSuggestedRu from '../../../assets/faq/ru/procrastination-suggested.png';
import reportsRu from '../../../assets/faq/ru/reports.png';
import billingPlansRu from '../../../assets/faq/ru/billing-plans.png';
import voiceRecordingRu from '../../../assets/faq/ru/voice-recording.png';
import voiceConsentRu from '../../../assets/faq/ru/voice-consent.png';
import voiceGatedRu from '../../../assets/faq/ru/voice-gated.png';

import backlogEn from '../../../assets/faq/en/backlog.png';
import languageSwitcherEn from '../../../assets/faq/en/language-switcher.png';
import taskFormEn from '../../../assets/faq/en/task-form.png';
import taskDetailEn from '../../../assets/faq/en/task-detail.png';
import procrastinationEnergyEn from '../../../assets/faq/en/procrastination-energy.png';
import procrastinationSuggestedEn from '../../../assets/faq/en/procrastination-suggested.png';
import reportsEn from '../../../assets/faq/en/reports.png';
import billingPlansEn from '../../../assets/faq/en/billing-plans.png';
import voiceRecordingEn from '../../../assets/faq/en/voice-recording.png';
import voiceConsentEn from '../../../assets/faq/en/voice-consent.png';
import voiceGatedEn from '../../../assets/faq/en/voice-gated.png';

import backlogBe from '../../../assets/faq/be/backlog.png';
import languageSwitcherBe from '../../../assets/faq/be/language-switcher.png';
import taskFormBe from '../../../assets/faq/be/task-form.png';
import taskDetailBe from '../../../assets/faq/be/task-detail.png';
import procrastinationEnergyBe from '../../../assets/faq/be/procrastination-energy.png';
import procrastinationSuggestedBe from '../../../assets/faq/be/procrastination-suggested.png';
import reportsBe from '../../../assets/faq/be/reports.png';
import billingPlansBe from '../../../assets/faq/be/billing-plans.png';
import voiceRecordingBe from '../../../assets/faq/be/voice-recording.png';
import voiceConsentBe from '../../../assets/faq/be/voice-consent.png';
import voiceGatedBe from '../../../assets/faq/be/voice-gated.png';

import backlogDe from '../../../assets/faq/de/backlog.png';
import languageSwitcherDe from '../../../assets/faq/de/language-switcher.png';
import taskFormDe from '../../../assets/faq/de/task-form.png';
import taskDetailDe from '../../../assets/faq/de/task-detail.png';
import procrastinationEnergyDe from '../../../assets/faq/de/procrastination-energy.png';
import procrastinationSuggestedDe from '../../../assets/faq/de/procrastination-suggested.png';
import reportsDe from '../../../assets/faq/de/reports.png';
import billingPlansDe from '../../../assets/faq/de/billing-plans.png';
import voiceRecordingDe from '../../../assets/faq/de/voice-recording.png';
import voiceConsentDe from '../../../assets/faq/de/voice-consent.png';
import voiceGatedDe from '../../../assets/faq/de/voice-gated.png';

import type { ScreenshotId } from './faqCatalog';

import type { SupportedLocale } from '../../../i18n';

export const FAQ_SCREENSHOTS: Record<ScreenshotId, Record<SupportedLocale, string>> = {
  backlog: {
    ru: backlogRu,
    en: backlogEn,
    be: backlogBe,
    de: backlogDe,
  },
  'language-switcher': {
    ru: languageSwitcherRu,
    en: languageSwitcherEn,
    be: languageSwitcherBe,
    de: languageSwitcherDe,
  },
  'task-form': {
    ru: taskFormRu,
    en: taskFormEn,
    be: taskFormBe,
    de: taskFormDe,
  },
  'task-detail': {
    ru: taskDetailRu,
    en: taskDetailEn,
    be: taskDetailBe,
    de: taskDetailDe,
  },
  'procrastination-energy': {
    ru: procrastinationEnergyRu,
    en: procrastinationEnergyEn,
    be: procrastinationEnergyBe,
    de: procrastinationEnergyDe,
  },
  'procrastination-suggested': {
    ru: procrastinationSuggestedRu,
    en: procrastinationSuggestedEn,
    be: procrastinationSuggestedBe,
    de: procrastinationSuggestedDe,
  },
  reports: {
    ru: reportsRu,
    en: reportsEn,
    be: reportsBe,
    de: reportsDe,
  },
  'billing-plans': {
    ru: billingPlansRu,
    en: billingPlansEn,
    be: billingPlansBe,
    de: billingPlansDe,
  },
  'voice-recording': {
    ru: voiceRecordingRu,
    en: voiceRecordingEn,
    be: voiceRecordingBe,
    de: voiceRecordingDe,
  },
  'voice-consent': {
    ru: voiceConsentRu,
    en: voiceConsentEn,
    be: voiceConsentBe,
    de: voiceConsentDe,
  },
  'voice-gated': {
    ru: voiceGatedRu,
    en: voiceGatedEn,
    be: voiceGatedBe,
    de: voiceGatedDe,
  },
};
